import React from "react";
import { Info, CheckCircle2, AlertCircle, PhoneCall, Mail  } from "lucide-react";

/** --- Visual primitives (same style as StatusMonitoringSection) --- */
const Karta = ({ children, style }) => (
  <div
    style={{
      border: "1px solid #e6e9ee",
      borderRadius: 16,
      padding: 16,
      background: "#fff",
      boxShadow: "0 16px 48px rgba(16,24,40,0.16), 0 8px 22px rgba(16,24,40,0.12)",
      ...style,
    }}
  >
    {children}
  </div>
);

const Sekcja = ({ title, icon: Icon = Info, children }) => (
  <Karta>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      {Icon ? <Icon size={18} color="#0b74e5" /> : null}
      <h3 style={{ margin: 0, fontSize: 18, color: "#0b74e5", textDecorationThickness: "2px", textUnderlineOffset: 4 }}>
        {title}
      </h3>
    </div>
    {children}
  </Karta>
);

const StatusCard = ({ title, status, tone = "info", subtitle }) => {
  const tones = {
    ok:   { top: "#22c55e", icon: "#22c55e", text: "#111827", sub: "#6b7280", border: "#e6f6ec", Icon: CheckCircle2 },
    bad:  { top: "#ef4444", icon: "#ef4444", text: "#111827", sub: "#6b7280", border: "#fdecec", Icon: AlertCircle  },
    warn: { top: "#f59e0b", icon: "#f59e0b", text: "#111827", sub: "#6b7280", border: "#fef3c7", Icon: AlertCircle  },
    info: { top: "#3b82f6", icon: "#3b82f6", text: "#111827", sub: "#6b7280", border: "#dbeafe", Icon: Info        },
  };
  const c = tones[tone] || tones.info;
  const IconComp = c.Icon;

  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 14,
        background: "#fff",
        boxShadow: "0 6px 24px rgba(16,24,40,0.06)",
        overflow: "hidden",
        display: "grid",
      }}
    >
      <div style={{ height: 6, background: c.top }} />
      <div style={{ display: "grid", gridTemplateColumns: "56px 1fr", gap: 12, padding: 14 }}>
        <div
          style={{
            display: "grid",
            placeItems: "center",
            height: 56,
            width: 56,
            borderRadius: "999px",
            border: `2px solid ${c.icon}`,
            color: c.icon,
          }}
        >
          <IconComp size={28} />
        </div>
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "#6b7280", fontWeight: 700 }}>
            {title}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: c.text }}>{status}</div>
          {subtitle ? <div style={{ fontSize: 12, color: c.sub }}>{subtitle}</div> : null}
        </div>
      </div>
    </div>
  );
};

/** Helpers */
const capitalize = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

const fmtNumber = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n.toLocaleString("pl-PL") : String(val);
};

/** Tone mapping incl. "-", empty, numeric thresholds */
// replace the mapper
function mapContactValueToTone(valueRaw, key) {
  const raw = valueRaw == null ? "" : String(valueRaw).trim();
  const isPlaceholder = raw === "" || raw === "-" || raw.toLowerCase() === "brak danych";

  // --- SPECIAL CASE: Zmiana adresu ---
  if (key === "address_change") {
    if (isPlaceholder) return { tone: "ok", display: "—" };   // green when no change
    return { tone: "bad", display: raw };                      // red when there was a change/date
  }

  // Missing / placeholder → neutral (blue)
  if (isPlaceholder) return { tone: "info", display: "—" };

  // Numeric → 0 green, >0 red
  const num = Number(raw);
  if (Number.isFinite(num)) {
    if (num === 0) return { tone: "ok",  display: "0" };
    return { tone: "bad", display: fmtNumber(num) };
  }

  // YES/NO (flipped)
  const yesRe = /^(tak|aktywny|aktywna|czynny|true|yes)$/i;
  const noRe  = /^(nie|false|no)$/i;
  if (yesRe.test(raw)) return { tone: "bad", display: capitalize(raw) }; // red
  if (noRe.test(raw))  return { tone: "ok",  display: capitalize(raw) }; // green

  // Explicit negatives still red
  if (/zawieszon|likwidacj|wykreśl|zamknięt/.test(raw.toLowerCase())) {
    return { tone: "bad", display: capitalize(raw) };
  }

  if (raw.toLowerCase() === "nie dotyczy") return { tone: "info", display: "Nie dotyczy" };

  // Default → neutral (blue)
  return { tone: "info", display: raw };
}


/** Section */
export default function ContactDataSection({ data = {}, emailsKrs = [] }) {
  // data: { key: { label_short, label_long, value } }
  const items = Object.entries(data).map(([key, obj]) => ({
    key,
    title: obj?.label_short || key,
    subtitle: obj?.label_long || "",
    value: obj?.value,
  }));

  const emails = Array.isArray(emailsKrs) ? emailsKrs.filter(Boolean) : [];

  if (items.length === 0 && emails.length === 0) {
    return (
      <Sekcja title="Dane kontaktowe">
        <em>Brak danych.</em>
      </Sekcja>
    );
  }

  return (
    <Sekcja title="Dane kontaktowe" icon={PhoneCall}>
      <style>{`
        .contactGrid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
        }
        @media (max-width: 1280px) { .contactGrid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 1024px) { .contactGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 640px)  { .contactGrid { grid-template-columns: 1fr; } }
      `}</style>

<div className="contactGrid">
  {/* 1) Existing mapped cards first */}
  {items.map(({ key, title, subtitle, value }) => {
    const { tone, display } = mapContactValueToTone(value, key);
    return (
      <StatusCard
        key={key}
        title={title}
        status={display}
        tone={tone}
        subtitle={subtitle}
      />
    );
  })}

  {/* 2) Email card LAST */}
  <StatusCard
    title="Adres e-mail (KRS)"
    status={Array.isArray(emailsKrs) ? emailsKrs.join(" • ") : (emailsKrs || "—")}
    tone="info"
    subtitle={emailsKrs?.length ? "Źródło: KRS" : "Źródło: KRS"}
  />
</div>

    </Sekcja>
  );
}
